<script setup lang="ts">
import { watch } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'

const props = defineProps<{ modelValue: string }>()
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const editor = useEditor({
  content: props.modelValue,
  extensions: [StarterKit],
  editorProps: {
    attributes: { class: 'outline-none min-h-[180px] px-4 py-3' }
  },
  onUpdate({ editor }) {
    emit('update:modelValue', editor.getHTML())
  }
})

watch(() => props.modelValue, (val) => {
  if (editor.value && editor.value.getHTML() !== val) {
    editor.value.commands.setContent(val, { emitUpdate: false })
  }
})
</script>

<template>
  <div class="rounded-lg overflow-hidden editor-wrap">
    <!-- Toolbar -->
    <div class="flex flex-wrap items-center gap-0.5 px-2 py-1.5 editor-toolbar">

      <!-- Bold -->
      <button type="button" @click="editor?.chain().focus().toggleBold().run()"
        :class="editor?.isActive('bold') ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="w-7 h-7 rounded flex items-center justify-center text-sm font-bold transition-colors" title="Bold">
        B
      </button>

      <!-- Italic -->
      <button type="button" @click="editor?.chain().focus().toggleItalic().run()"
        :class="editor?.isActive('italic') ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="w-7 h-7 rounded flex items-center justify-center text-sm italic font-medium transition-colors" title="Italic">
        I
      </button>

      <div class="w-px h-5 mx-1 editor-divider"></div>

      <!-- H2 -->
      <button type="button" @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()"
        :class="editor?.isActive('heading', { level: 2 }) ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="px-2 h-7 rounded flex items-center justify-center text-xs font-bold transition-colors" title="Heading 2">
        H2
      </button>

      <!-- H3 -->
      <button type="button" @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()"
        :class="editor?.isActive('heading', { level: 3 }) ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="px-2 h-7 rounded flex items-center justify-center text-xs font-bold transition-colors" title="Heading 3">
        H3
      </button>

      <div class="w-px h-5 mx-1 editor-divider"></div>

      <!-- Bullet list -->
      <button type="button" @click="editor?.chain().focus().toggleBulletList().run()"
        :class="editor?.isActive('bulletList') ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="w-7 h-7 rounded flex items-center justify-center transition-colors" title="Bullet list">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="4" cy="6" r="1.5"/>
          <circle cx="4" cy="12" r="1.5"/>
          <circle cx="4" cy="18" r="1.5"/>
          <rect x="8" y="5" width="12" height="2" rx="1"/>
          <rect x="8" y="11" width="12" height="2" rx="1"/>
          <rect x="8" y="17" width="12" height="2" rx="1"/>
        </svg>
      </button>

      <!-- Ordered list -->
      <button type="button" @click="editor?.chain().focus().toggleOrderedList().run()"
        :class="editor?.isActive('orderedList') ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="w-7 h-7 rounded flex items-center justify-center transition-colors" title="Ordered list">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 5h1.5v3H3V5zm0 6h1.5v3H3v-3zm0 6h1.5v3H3v-3z"/>
          <rect x="8" y="5" width="12" height="2" rx="1"/>
          <rect x="8" y="11" width="12" height="2" rx="1"/>
          <rect x="8" y="17" width="12" height="2" rx="1"/>
        </svg>
      </button>

      <div class="w-px h-5 mx-1 editor-divider"></div>

      <!-- Blockquote -->
      <button type="button" @click="editor?.chain().focus().toggleBlockquote().run()"
        :class="editor?.isActive('blockquote') ? 'bg-[#4F8646] text-white' : 'text-[#475569] hover:bg-slate-200'"
        class="w-7 h-7 rounded flex items-center justify-center transition-colors" title="Blockquote">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 10h4v4H3zm0-6h4v4H3zm7 6h4v4h-4zm0-6h4v4h-4z" opacity=".3"/>
          <path d="M14 17v-3.5c0-.83-.67-1.5-1.5-1.5H9v-2h5V8H9c-.83 0-1.5.67-1.5 1.5v5c0 .83.67 1.5 1.5 1.5h3.5V17h2zm-9 0v-3.5c0-.83-.67-1.5-1.5-1.5H0v-2h5V8H0v2h3.5V17H5z"/>
        </svg>
      </button>

      <!-- Horizontal rule -->
      <button type="button" @click="editor?.chain().focus().setHorizontalRule().run()"
        class="w-7 h-7 rounded flex items-center justify-center text-[#475569] hover:bg-slate-200 transition-colors" title="Horizontal rule">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12h16" />
        </svg>
      </button>

      <div class="flex-1"></div>

      <!-- Undo -->
      <button type="button" @click="editor?.chain().focus().undo().run()" :disabled="!editor?.can().undo()"
        class="w-7 h-7 rounded flex items-center justify-center text-[#475569] hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Undo">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6" />
        </svg>
      </button>

      <!-- Redo -->
      <button type="button" @click="editor?.chain().focus().redo().run()" :disabled="!editor?.can().redo()"
        class="w-7 h-7 rounded flex items-center justify-center text-[#475569] hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Redo">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6M21 10l-6-6" />
        </svg>
      </button>
    </div>

    <!-- Editor area -->
    <EditorContent :editor="editor" class="text-[#0F172A] text-sm" />
  </div>
</template>

<style scoped>
/* ── Editor chrome ── */
.editor-wrap    { border: 1px solid #E2E8F0; }
.editor-toolbar { border-bottom: 1px solid #E2E8F0; background: #F8FAFC; }
.editor-divider { background: #E2E8F0; }

/* Tiptap placeholder */
:deep(.tiptap p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  color: #94A3B8;
  pointer-events: none;
  float: left;
  height: 0;
}
/* Lists inside editor — override Tailwind preflight resets */
:deep(.tiptap ul) { list-style-type: disc !important; padding-left: 1.5rem !important; margin: 0.5rem 0; }
:deep(.tiptap ol) { list-style-type: decimal !important; padding-left: 1.5rem !important; margin: 0.5rem 0; }
:deep(.tiptap li) { display: list-item !important; margin-bottom: 0.2rem; }
:deep(.tiptap h2) { font-size: 1.25rem; font-weight: 700; margin: 0.75rem 0 0.25rem; }
:deep(.tiptap h3) { font-size: 1.05rem; font-weight: 600; margin: 0.6rem 0 0.2rem; }
:deep(.tiptap blockquote) {
  border-left: 3px solid #4F8646;
  padding-left: 0.75rem;
  color: #64748B;
  margin: 0.5rem 0;
}
:deep(.tiptap hr) { border: none; border-top: 1px solid #E2E8F0; margin: 0.75rem 0; }
</style>
